import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, IconButton, Typography, useTheme, useMediaQuery, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CropIcon from '@mui/icons-material/Crop';
import CheckIcon from '@mui/icons-material/Check';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import CropFreeIcon from '@mui/icons-material/CropFree';
import Cropper from 'react-cropper';
import '../styles/cropper.css';

const CropModal = ({ imageFile, onCropComplete, onClose, aspectRatio: aspectRatioProp, hideAspectRatioControls }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const cropperRef = useRef(null);
  const [aspectRatio, setAspectRatio] = useState(aspectRatioProp ?? null);
  const [viewportHeight, setViewportHeight] = useState('100vh');
  const [safeAreaBottom, setSafeAreaBottom] = useState(0);

  // 모바일 브라우저의 viewport 높이와 safe-area-inset을 동적으로 계산
  useEffect(() => {
    const updateViewportHeight = () => {
      // 실제 viewport 높이 계산 (모바일 브라우저 주소창 고려)
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      setViewportHeight(`${window.innerHeight}px`);
      
      // safe-area-inset-bottom 계산 (CSS 변수 활용)
      const computedStyle = getComputedStyle(document.documentElement);
      const safeAreaBottomValue = parseInt(computedStyle.getPropertyValue('--safe-area-inset-bottom')) || 0;
      
      // iOS Safari에서 추가 보정
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        setSafeAreaBottom(Math.max(safeAreaBottomValue, 20));
      } else {
        setSafeAreaBottom(Math.max(safeAreaBottomValue, 8));
      }
    };

    // 초기 설정
    updateViewportHeight();

    // resize 이벤트 리스너 (모바일 브라우저 주소창 숨김/표시 시)
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    // visualViewport API 사용 (더 정확한 viewport 정보)
    if (window.visualViewport) {
      const handleVisualViewportChange = () => {
        updateViewportHeight();
      };
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      
      return () => {
        window.removeEventListener('resize', updateViewportHeight);
        window.removeEventListener('orientationchange', updateViewportHeight);
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
      };
    }

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  React.useEffect(() => {
    if (aspectRatioProp !== undefined) {
      setAspectRatio(aspectRatioProp);
      if (cropperRef.current) {
        cropperRef.current.cropper.setAspectRatio(aspectRatioProp);
      }
    }
  }, [aspectRatioProp]);

  const handleAspectRatioChange = (event, newRatio) => {
    if (aspectRatioProp !== undefined) return;
    setAspectRatio(newRatio);
    if (cropperRef.current) {
      const cropper = cropperRef.current.cropper;
      cropper.setAspectRatio(newRatio);
    }
  };

  const compressToUnder4MB = async (canvas, initialQuality = 0.88) => {
    let quality = initialQuality;
    let blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));
    while (blob && blob.size > 4 * 1024 * 1024 && quality > 0.5) {
      quality -= 0.08;
      blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));
    }
    return blob;
  };

  const handleCropComplete = async () => {
    if (cropperRef.current) {
      const cropper = cropperRef.current.cropper;
      let width = 900, height = 1200;
      if (aspectRatioProp === 3/4 || aspectRatioProp === 0.75) {
        width = 900; height = 1200;
      }
      const croppedCanvas = cropper.getCroppedCanvas({
        width,
        height,
        imageSmoothingQuality: 'high'
      });
      if (aspectRatioProp === 3/4 || aspectRatioProp === 0.75) {
        // BookMemoEdit(책 썸네일 등록)에서만 반복 압축
        const blob = await compressToUnder4MB(croppedCanvas, 0.88);
        onCropComplete(blob);
      } else {
        croppedCanvas.toBlob((blob) => {
          onCropComplete(blob);
        }, 'image/jpeg', 0.95);
      }
    }
  };

  // 하단 버튼 영역의 실제 높이 계산
  const buttonAreaHeight = 80; // 버튼 높이(48px) + 상하 패딩(16px) + 여유공간(16px)
  const safeAreaBottomPadding = Math.max(safeAreaBottom, 8); // 최소 8px 보장

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 0,
        bgcolor: '#000',
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '600px',
        height: viewportHeight, // 동적 viewport 높이 사용
        overflow: 'hidden'
      }}
    >
      {/* 헤더 영역 제거됨 */}
      
      {/* 크롭 영역 */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: 0,
          // 하단 버튼 영역을 정확히 제외한 높이 계산
          height: `calc(${viewportHeight} - ${buttonAreaHeight}px - ${safeAreaBottomPadding}px)`,
          bgcolor: '#000',
          pb: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          overflow: 'hidden',
        }}
      >
        <Cropper
          ref={cropperRef}
          src={imageFile ? URL.createObjectURL(imageFile) : ''}
          style={{ 
            height: `calc(${viewportHeight} - ${buttonAreaHeight}px - ${safeAreaBottomPadding}px)`,
            width: '96%',
            maxWidth: '100%',
            objectFit: 'contain',
            flexShrink: 0,
          }}
          aspectRatio={aspectRatio}
          viewMode={2}
          responsive={true}
          autoCropArea={1}
          guides={true}
          center={false}
          highlight={false}
          cropBoxMovable={true}
          cropBoxResizable={true}
          toggleDragModeOnDblclick={false}
          rotatable={false}
          scalable={false}
          zoomable={true}
          zoomOnTouch={true}
          zoomOnWheel={true}
          wheelZoomRatio={0.1}
          minCropBoxWidth={100}
          minCropBoxHeight={100}
          ready={() => {
            if (cropperRef.current) {
              const cropper = cropperRef.current.cropper;
              cropper.setCanvasData({
                top: 0,
                left: (cropper.getContainerData().width - cropper.getCanvasData().width) / 2
              });
            }
          }}
        />
      </Box>
      
      {/* 하단 컨트롤 영역: 취소/크롭 완료 버튼 */}
      <Box sx={{ 
        px: 2, 
        bgcolor: 'transparent',
        position: 'fixed',
        zIndex: 1500,
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: `${safeAreaBottomPadding}px`, // safe-area-inset-bottom 고려
        width: '100%',
        maxWidth: '600px',
        height: `${buttonAreaHeight}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        m: 0,
        mt: 0,
        // 모바일에서 추가 안전장치
        '@media (max-width: 600px)': {
          bottom: `${Math.max(safeAreaBottomPadding, 16)}px`, // 최소 16px 보장
          height: `${Math.max(buttonAreaHeight, 88)}px`, // 최소 88px 보장
          paddingBottom: 'env(safe-area-inset-bottom, 0px)', // 추가 안전장치
        },
        // iOS Safari에서 추가 보정
        '@supports (padding: max(0px))': {
          bottom: `max(${safeAreaBottomPadding}px, env(safe-area-inset-bottom, 0px))`,
        }
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          gap: 1,
          flexDirection: 'row',
          width: '100%',
          m: 0,
          mt: 0,
        }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              bgcolor: '#fff',
              color: '#111',
              fontWeight: 700,
              border: '1.5px solid #bbb',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              width: '33%',
              minWidth: 0,
              height: '48px', // 명시적 높이 설정
              borderRadius: '4px',
              '&:hover': {
                bgcolor: '#f0f0f0',
                borderColor: '#888',
                color: '#111'
              }
            }}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleCropComplete}
            startIcon={<CheckIcon />}
            sx={{
              bgcolor: '#111',
              color: '#fff',
              boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              fontWeight: 700,
              border: '1.5px solid #bbb',
              width: '100%',
              minWidth: 0,
              height: '48px', // 명시적 높이 설정
              borderRadius: '4px',
              '&:hover': {
                bgcolor: '#f0f0f0',
                color: '#111'
              }
            }}
          >
            크롭 완료
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CropModal; 